import type { ClaimWithRelations } from "../api/use-claim";

export type ClaimLegalStepName = ClaimWithRelations["claimLegalStep"]["name"];

/** Mirrors the Vue constants (seeded ids + safelisted colors). */
interface LegalStepInfo {
  id: number;
  color: string;
  displayText: string;
}

export const legalSteps: Record<ClaimLegalStepName, LegalStepInfo> = {
  CLAIM_VALIDATION: { id: 0, color: "teal", displayText: "Validation" },
  UPCLAIM: { id: 1, color: "blue", displayText: "Amiable" },
  CONCILIATION: { id: 2, color: "amber", displayText: "Précontentieux" },
  COURT: { id: 3, color: "red", displayText: "Contentieux" },
  BAILIFF: { id: 4, color: "indigo", displayText: "Huissier" },
  CANCELLED: { id: 5, color: "gray", displayText: "Abandon" },
  LOST: { id: 6, color: "gray", displayText: "Perdu" },
  WAITING_FOR_MONEY: { id: 7, color: "violet", displayText: "Attente de paiement" },
  MONEY_RECEIVED: { id: 8, color: "green", displayText: "Paiement reçu" },
  PAYMENT_SENT: { id: 9, color: "lime", displayText: "Paiement envoyé" },
};

/** Étapes d'un dossier gagné : la compagnie a été condamnée ou a accepté de payer. */
const WON_LEGAL_STEPS: ClaimLegalStepName[] = ["WAITING_FOR_MONEY", "MONEY_RECEIVED", "PAYMENT_SENT"];

export function isClaimWon(stepName: ClaimLegalStepName): boolean {
  return WON_LEGAL_STEPS.includes(stepName);
}

export function claimLegalStepById(id: number): ClaimLegalStepName {
  const entry = Object.entries(legalSteps).find(([, step]) => step.id === id);
  if (!entry) {
    throw new Error(`No claim step found with id ${id}`);
  }
  return entry[0] as ClaimLegalStepName;
}

/**
 * Tailwind can't resolve dynamic class names, so the `text-{color}-800 …`
 * combinations used by the Vue app are enumerated statically here.
 */
export const legalStepAlertClasses: Record<ClaimLegalStepName, string> = {
  CLAIM_VALIDATION: "text-teal-800 border-teal-300 bg-teal-50",
  UPCLAIM: "text-blue-800 border-blue-300 bg-blue-50",
  CONCILIATION: "text-amber-800 border-amber-300 bg-amber-50",
  COURT: "text-red-800 border-red-300 bg-red-50",
  BAILIFF: "text-indigo-800 border-indigo-300 bg-indigo-50",
  CANCELLED: "text-gray-800 border-gray-300 bg-gray-50",
  LOST: "text-gray-800 border-gray-300 bg-gray-50",
  WAITING_FOR_MONEY: "text-violet-800 border-violet-300 bg-violet-50",
  MONEY_RECEIVED: "text-green-800 border-green-300 bg-green-50",
  PAYMENT_SENT: "text-lime-800 border-lime-300 bg-lime-50",
};

/** DotLegalStep.vue colors (halo / core). */
export const legalStepDotClasses: Record<ClaimLegalStepName, { halo: string; core: string }> = {
  CLAIM_VALIDATION: { halo: "bg-teal-500/20", core: "bg-teal-500" },
  UPCLAIM: { halo: "bg-blue-500/20", core: "bg-blue-500" },
  CONCILIATION: { halo: "bg-amber-500/20", core: "bg-amber-500" },
  COURT: { halo: "bg-red-500/20", core: "bg-red-500" },
  BAILIFF: { halo: "bg-indigo-500/20", core: "bg-indigo-500" },
  CANCELLED: { halo: "bg-gray-500/20", core: "bg-gray-500" },
  LOST: { halo: "bg-gray-500/20", core: "bg-gray-500" },
  WAITING_FOR_MONEY: { halo: "bg-violet-500/20", core: "bg-violet-500" },
  MONEY_RECEIVED: { halo: "bg-green-500/20", core: "bg-green-500" },
  PAYMENT_SENT: { halo: "bg-lime-500/20", core: "bg-lime-500" },
};

export const legalStepBadgeClasses: Record<ClaimLegalStepName, string> = {
  CLAIM_VALIDATION: "bg-teal-50 text-teal-700 ring-teal-600/20",
  UPCLAIM: "bg-blue-50 text-blue-700 ring-blue-600/20",
  CONCILIATION: "bg-amber-50 text-amber-700 ring-amber-600/20",
  COURT: "bg-red-50 text-red-700 ring-red-600/20",
  BAILIFF: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  CANCELLED: "bg-gray-50 text-gray-700 ring-gray-600/20",
  LOST: "bg-gray-50 text-gray-700 ring-gray-600/20",
  WAITING_FOR_MONEY: "bg-violet-50 text-violet-700 ring-violet-600/20",
  MONEY_RECEIVED: "bg-green-50 text-green-700 ring-green-600/20",
  PAYMENT_SENT: "bg-lime-50 text-lime-700 ring-lime-600/20",
};
